import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";

import { useEffect, useState } from "react";
import axios from "axios.js";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

function LeadConnectorCard({
  bucketRef,
  triggerRefresh,
  leadFields,
  leadConnector,
  dataForms,
  openDialog,
  onClose,
}) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [leadAttributes, setLeadAttributes] = useState([]);
  const [mappedFields, setMappedFields] = useState({});
  const [formFields, setFormFields] = useState({});
  const [selectedDataForm, setSelectedDataForm] = useState(null);

  useEffect(() => {
    leadFields = [
      { code: "lead_name", title: "Name" },
      { code: "lead_phone_number", title: "Phone Number" },
      { code: "lead_email_address", title: "Email Address" },
      ...leadFields,
    ];
    setLeadAttributes(leadFields);
  }, []);

  useEffect(() => {
    setMappedFields({});
    if (!selectedDataForm) {
      return;
    }
    const dfs = [];
    selectedDataForm.form.sections.forEach((sec) => {
      sec.fields.every((f) => {
        dfs.push(f);
        return true;
      });
    });
    setFormFields(dfs);
  }, [selectedDataForm]);

  function buildLeadAttributeRows() {
    const fields = [];
    leadAttributes.map((field) => {
      fields.push(
        <MDBox mb={3}>
          <MDBox display="flex" justifyContent="space-between">
            <MDBox>
              <MDTypography variant="h6" fontSize={14} fontWeight="light">
                <b>{field.title}</b>
                <br />(
                {["lead_email_address", "lead_phone_number"].includes(field.code)
                  ? "Optional"
                  : "Required"}
                )
              </MDTypography>
            </MDBox>
            <MDBox>
              <MDTypography variant="h6" fontSize={14} mt={2}>
                <Icon variant="outlined">east</Icon>
              </MDTypography>
            </MDBox>
            <MDBox mt={0.5}>
              <FormControl sx={{ minWidth: 130, width: "100%" }}>
                <InputLabel>Select Field</InputLabel>
                <Select
                  label="Select Field"
                  onChange={(e) => {
                    const temp = mappedFields;
                    temp[field.code] = e.target.value;
                    setMappedFields(temp);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    formFields.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      formFields.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.display_name}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </MDBox>
      );
    });
    return fields;
  }

  async function connectLeadBucket() {
    if (selectedDataForm === null) {
      setSeverity("error");
      setErrMsg("Please select a data form.");
      setOpen(true);
      return;
    }

    if (mappedFields.length <= 0) {
      setSeverity("error");
      setErrMsg("Please map lead attributes to your form fields.");
      setOpen(true);
      return;
    }

    let errors = 0;
    Object.keys(mappedFields).forEach((code) => {
      if (
        (typeof mappedFields[code] === "undefined" || mappedFields[code] === null) &&
        !["lead_phone_number", "lead_email_address"].includes(code)
      ) {
        setSeverity("error");
        setErrMsg("Map all required attributes.");
        setOpen(true);
        errors += 1;
      }
    });

    if (errors > 0) {
      return;
    }

    if (
      (typeof mappedFields.lead_phone_number === "undefined" ||
        mappedFields.lead_phone_number === null) &&
      (typeof mappedFields.lead_email_address === "undefined" ||
        mappedFields.lead_email_address === null)
    ) {
      setSeverity("error");
      setErrMsg("Email address or Phone number is required");
      setOpen(true);
      return;
    }

    onClose(false);
    setShowLoading(true);

    const json = {
      form: selectedDataForm.reference,
      bucket: bucketRef,
      fields: mappedFields,
    };

    await axios
      .post("portal/data_forms/leads/connect.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function deleteConnector(ref) {
    onClose(false);
    setShowLoading(true);
    await axios
      .get(`/portal/data_forms/leads/${ref}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openDialog} fullWidth maxWidth={leadConnector ? "xs" : "sm"}>
        <DialogTitle>
          <MDBox>Data Form Connector</MDBox>
        </DialogTitle>
        <DialogContent>
          {leadConnector !== null ? (
            <MDBox px={1} mb={3}>
              <MDBox display="flex" justifyContent="space-between">
                <MDBox>
                  <MDTypography variant="h6" fontSize={12} fontWeight="light">
                    Data Form
                  </MDTypography>
                  <MDTypography variant="h6" fontSize={16} fontWeight="regular">
                    {leadConnector.form}
                  </MDTypography>
                </MDBox>
                <MDBox>
                  <MDButton
                    variant="outlined"
                    color="error"
                    iconOnly
                    size="small"
                    onClick={() => {
                      if (window.confirm("Delete connected data form?") === true) {
                        deleteConnector(leadConnector.reference);
                      }
                    }}
                  >
                    <Icon>delete</Icon>
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          ) : (
            <MDBox px={1} mb={5}>
              <MDBox>
                <Alert severity="info">Connect a data form to this bucket.</Alert>
              </MDBox>
              <MDBox mt={5}>
                <FormControl sx={{ minWidth: 120, width: "100%" }}>
                  <InputLabel>Select Data Form</InputLabel>
                  <Select
                    label="Select Data Form"
                    onChange={(e) => {
                      dataForms.forEach((dataform) => {
                        if (dataform.reference === e.target.value) {
                          setSelectedDataForm(dataform);
                        }
                      });
                    }}
                    sx={{ padding: "12px", paddingLeft: "3px" }}
                  >
                    {
                      // --> Notice below changes
                      dataForms.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                        dataForms.map((record) => (
                          <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                        ))
                    }
                  </Select>
                </FormControl>
              </MDBox>
              <MDBox>
                {selectedDataForm !== null && (
                  <MDBox mt={4}>
                    <MDTypography
                      pl={0}
                      mb={2}
                      variant="h6"
                      fontSize={16}
                      fontWeight="regular"
                      align="center"
                    >
                      Map lead attributes to form fields.
                    </MDTypography>
                    <MDBox>
                      <Divider />
                    </MDBox>
                    <MDBox display="flex" justifyContent="space-between">
                      <MDBox>
                        <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                          Lead Attribute
                        </MDTypography>
                      </MDBox>
                      <MDBox pr={1}>
                        <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                          Form Field
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    <MDBox>
                      <Divider />
                    </MDBox>
                    <MDBox>{buildLeadAttributeRows().map((row) => row)}</MDBox>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              onClose(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          {!leadConnector && (
            <MDButton
              color="dark"
              variant="contained"
              onClick={() => {
                connectLeadBucket();
              }}
              startIcon={<Icon>electrical_services</Icon>}
            >
              Connect
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

export default LeadConnectorCard;
